import { useEffect, useMemo, useRef, useState } from "react";
import { Button, DialogContent, Stack, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { CircularLoading } from "@/components/common";
import { CommonDialog } from "@/components/common/CommonDialog";
import { useInterventionsContext } from "@/contexts/InterventionsContext";
import { useLocationContext } from "@/contexts/LocationContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { PrescriptionCare } from "@/pages/medicalcare/components/PrescriptionCare/PrescriptionCare";
import { ExamsFields } from "@/pages/medicalcare/components/ExamsFields";
import { ListaDeIntervenções } from "@/pages/medicalcare/components/ListaDeIntervenções";
import { SelectProtocolDialog } from "@/pages/medicalcare/components/SelectProtocolDialog";
import {
  DocumentVisualization,
  readDocSlot,
  readFirstDocSlot,
  releaseDocIds,
} from "@/pages/medicalcare/components/DocumentVisualization/DocumentVisualization";
import { SignatureDialog } from "@/pages/medicalcare/components/prescription/SignatureDialog";
import { mapMedicationRequestToIntervention } from "@/pages/medicalcare/utils/mapMedicationRequestToIntervention";
import { enrichMedicationsFromCmed } from "@/pages/medicalcare/utils/enrichMedicationInterventionFromCmed";
import { mapServiceRequestToIntervention } from "@/pages/medicalcare/utils/mapServiceRequestToIntervention";
import {
  buildBasedOnXmlForDocumentItems,
  shouldUpsertMedication,
  shouldUpsertService,
} from "@/pages/medicalcare/utils/footerInterventionPersist";
import { extractDocumentReferenceAuthorData } from "@/utils/fhirDocumentUtils";
import { Base64SignPDF } from "@/services/dsig";
import { GetPatient, PostMedicationRequest } from "@/services/fhir";
import { PostDocumentReference } from "@/services/fhir/patientsummary/documentreference/PostDocumentReference";
import { PostServiceRequest } from "@/services/fhir/patientsummary/serviceRequest/PostServiceRequest";
import { useFhirData } from "@/utils/useFhirData";
import type { ReissueDocumentType } from "@/pages/patientsearch/components/DocumentReissueShortcutDialog";
import {
  buildReissueEncounterReference,
  type ReissueEncounterReference,
} from "./buildReissueEncounterReference";
import {
  collectReissuePdfGroups,
  hasItemOutsideOriginalGroups,
} from "./reissuePdfGroup";

/** Um dialog por vez — evita sobreposição. */
type ActiveDialog = "edit" | "sign" | "protocol";

interface ReissueEditInterventionsStepProps {
  patientId: string;
  patientName?: string;
  documentType: ReissueDocumentType;
  documentRow: any;
  onBack: () => void;
  /** Chamado após assinatura bem-sucedida (volta à lista de documentos). */
  onDone: () => void;
  /**
   * Chamado ao fim da tentativa de assinatura (sucesso ou erro) para recarregar
   * a lista de documentos — no erro pode ter havido POST parcial.
   */
  onFinished?: () => void;
}

function pickPdfBase64(base64: any): string | undefined {
  return (
    base64?.[0]?.[`special-0`]?.base64?.split(",")?.[1] ||
    base64?.[0]?.["simple-medication"]?.base64?.split(",")?.[1] ||
    base64?.[0]?.["simple-supplement"]?.base64?.split(",")?.[1] ||
    base64?.[0]?.["simple-herbalMedicine"]?.base64?.split(",")?.[1] ||
    base64?.[0]?.exam?.base64?.split(",")?.[1] ||
    base64?.[0]?.simple?.base64?.split(",")?.[1]
  );
}

function dialogTitle(documentType: ReissueDocumentType): string {
  if (documentType === "prescription") return "Reemissão de receita médica";
  if (documentType === "solicitation") return "Reemissão de solicitação";
  return "Reemissão de documento";
}

/**
 * Remissão na agenda: Dialog de ajuste, protocolo e assinatura
 * são mutuamente exclusivos (abre um → fecha o outro).
 */
export function ReissueEditInterventionsStep({
  patientId,
  patientName,
  documentType,
  documentRow,
  onBack,
  onDone,
  onFinished,
}: ReissueEditInterventionsStepProps) {
  const { user } = useSessionContext();
  const { userPractitioner, userPractitionerRole, userSpecialties } =
    usePractitionerRoleContext();
  const { organization } = useOrganizationContext();
  const { location } = useLocationContext();
  const {
    base64,
    setInterventionsList,
    setBase64,
    selectedInterventions,
    functions,
    clean,
    setClean,
    control,
    handleSubmit,
    isEdit,
    setValue,
    expandedList,
    setExpandedList,
    setCreateDialog,
    setSelectProtocol,
    appliedProtocol,
    setAppliedProtocol,
    action,
    setSelectedItem,
  } = useInterventionsContext();

  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>("edit");
  const [encounterReference, setEncounterReference] =
    useState<ReissueEncounterReference | null>(null);
  const [patientData, setPatientData] = useState<any>();
  const [locationData, setLocationData] = useState<any>();
  // Grupos de PDF do documento original — edição não pode mover item para outro PDF
  const originalPdfGroupsRef = useRef<Set<string>>(new Set());

  const authorData = useMemo(
    () => extractDocumentReferenceAuthorData(documentRow?.fullResource),
    [documentRow?.fullResource],
  );

  const pdfPreviewBase64 = pickPdfBase64(base64);

  // Hidrata itens do documento + resolve Encounter/Appointment
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setActiveDialog("edit");
      setInterventionsList([]);
      setBase64(undefined);
      // selectedItem é global do atendimento; índice velho editaria a linha errada.
      setSelectedItem(undefined);
      originalPdfGroupsRef.current = new Set();

      try {
        const docResource = documentRow?.fullResource;
        const relatesTo = authorData?.relatesTo;
        const documentPeriod = docResource?.period
          ? { start: docResource.period.start, end: docResource.period.end }
          : undefined;

        const encounterRef = await buildReissueEncounterReference({
          documentResource: docResource,
          patientId,
          practitionerRoleId: userPractitionerRole?.id,
          practitionerDisplay: user?.name,
        });

        if (cancelled) return;
        setEncounterReference(encounterRef);

        if (documentType === "prescription") {
          const resources =
            documentRow?.fullMedicationResource
              ?.map((e: any) => e?.resource)
              ?.filter(Boolean) || [];

          const mapped = resources.map(mapMedicationRequestToIntervention);
          const enriched = await enrichMedicationsFromCmed(mapped);

          if (cancelled) return;

          const hydrated = enriched.map((el: any) => ({
            ...el,
            refact: true,
            sign: false,
            documentReferenceId: docResource?.id,
            ...(relatesTo && { documentRelatesTo: relatesTo }),
            ...(documentPeriod && { documentPeriod }),
          }));
          // Guarda os grupos originais para validar edições antes de assinar
          originalPdfGroupsRef.current = collectReissuePdfGroups(hydrated);
          setInterventionsList(hydrated);
        } else if (documentType === "solicitation") {
          const resources =
            documentRow?.fullServiceResource
              ?.map((e: any) => e?.resource)
              ?.filter(Boolean) || [];

          const mapped = resources.map(mapServiceRequestToIntervention);

          if (cancelled) return;

          const hydrated = mapped.map((el: any) => ({
            ...el,
            refact: true,
            sign: false,
            documentReferenceId: docResource?.id,
            ...(relatesTo && { documentRelatesTo: relatesTo }),
            ...(documentPeriod && { documentPeriod }),
          }));
          // Guarda os grupos originais para validar edições antes de assinar
          originalPdfGroupsRef.current = collectReissuePdfGroups(hydrated);
          setInterventionsList(hydrated);
        } else {
          toast.info(
            "Edição de atestados/relatórios na remissão ainda não está disponível.",
          );
        }
      } catch (err) {
        console.error("[ReissueEditInterventionsStep] hidratação:", err);
        toast.error("Não foi possível carregar os itens do documento.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentRow?.id, documentRow?.fullResource?.id, documentType, patientId]);

  // Paciente + local para o PDF
  useEffect(() => {
    if (!encounterReference) return;
    let cancelled = false;
    (async () => {
      try {
        const patient = await GetPatient({ identifier: patientId });
        if (!cancelled) setPatientData(patient?.data);

        const locationReference = useFhirData.findParticipant(
          encounterReference?.participant,
          "Location",
        );
        const selectedLocation = location?.find((el: any) =>
          el?.resource?.id?.includes(
            locationReference?.reference?.split("/")?.[1],
          ),
        )?.resource;
        if (!cancelled) setLocationData(selectedLocation);
      } catch (err) {
        console.error("[ReissueEditInterventionsStep] setup PDF:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId, encounterReference, location]);

  // Limpa ao desmontar a sessão de remissão
  useEffect(() => {
    return () => {
      setInterventionsList([]);
      setBase64(undefined);
      setAppliedProtocol([]);
      setSelectProtocol(false);
      setSelectedItem(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBack = () => {
    setInterventionsList([]);
    setBase64(undefined);
    setSelectProtocol(false);
    setSelectedItem(undefined);
    onBack();
  };

  /** Fecha ajuste e abre assinatura (sem sobrepor). */
  const handleContinueToSign = () => {
    if (!encounterReference?.id && !encounterReference?.appointmentId) {
      toast.warn(
        "Não foi possível resolver o atendimento vinculado ao documento.",
      );
      return;
    }
    if (!selectedInterventions?.all?.length) {
      toast.warn("Adicione ao menos um item antes de reemitir.");
      return;
    }
    // Item que mudou de grupo (aba, tarja especial, categoria) iria para outro PDF não assinado
    if (
      hasItemOutsideOriginalGroups(
        selectedInterventions.all,
        originalPdfGroupsRef.current,
      )
    ) {
      toast.warn(
        "Na reemissão não é possível mudar o tipo, a tarja ou a categoria de um item. Desfaça a alteração para continuar.",
      );
      return;
    }
    if (!pickPdfBase64(base64)) {
      toast.warn("Gerando o documento… aguarde um instante e tente novamente.");
      return;
    }
    setActiveDialog("sign");
  };

  const openProtocolDialog = () => {
    setSelectProtocol(true);
    setActiveDialog("protocol");
  };

  const closeProtocolDialog = () => {
    setSelectProtocol(false);
    setActiveDialog("edit");
  };

  const closeSignDialog = () => {
    setActiveDialog("edit");
  };

  /** Reemissão não aceita itens novos (ficariam fora do PDF) — só edição de existentes. */
  const blockedAdd = () => {
    toast.info("Selecione um item da lista para editar.");
  };

  const docReferenceMeta = {
    organization: {
      id: organization?.id || authorData.organizationId,
      alias: organization?.alias?.[0] || authorData.organizationAlias,
      name: organization?.name || authorData.organizationName,
    },
    practitioner: {
      id: userPractitioner?.id || authorData.practitionerId,
      name: userPractitioner?.name?.[0]?.text || authorData.practitionerName,
    },
    servicePratice: authorData.practiceSetting || {
      display: userSpecialties?.[0]?.coding?.[0]?.display,
      code: userSpecialties?.[0]?.coding?.[0]?.code,
      system: "https://fluxmed.com.br/fhir/CodeSystem/CBO",
    },
    time: {
      start: documentRow?.fullResource?.period?.start,
      end: documentRow?.fullResource?.period?.end,
    },
    relatesTo: authorData.relatesTo,
    codeValue: documentRow?.fullResource?.type?.coding?.[0]?.code,
    codeDisplay: documentRow?.fullResource?.type?.coding?.[0]?.display,
    categoryValue: documentRow?.fullResource?.category?.[0]?.coding?.[0]?.code,
    categoryDisplay:
      documentRow?.fullResource?.category?.[0]?.coding?.[0]?.display,
  };

  const handleSignature = async (
    provider: "vidaas" | "safeid",
    password?: string,
    lifeTime?: number,
  ) => {
    try {
      setSigning(true);
      toast.warn("Aguardando verificação e segurança do seu provedor!");

      const pdfPayload = pickPdfBase64(base64);
      if (!pdfPayload) {
        toast.warn("PDF ainda não está pronto. Aguarde e tente novamente.");
        return;
      }

      const signResponse = await Base64SignPDF({
        provider,
        ...(provider === "safeid" && { userPassword: password }),
        pdfBase64: pdfPayload,
        cpfCnpj: user?.username,
        lifeTime: lifeTime ?? 0,
      });

      if (!signResponse?.length) throw new Error("Assinatura vazia");

      const references = encounterReference;
      if (!references) throw new Error("encounterReference ausente");

      if (documentType === "prescription") {
        const meds =
          selectedInterventions?.all?.filter(
            (el: any) =>
              el?.type === "medication" ||
              el?.type === "supplement" ||
              el?.type === "herbalMedicine",
          ) || [];
        const toUpsert = meds.filter(shouldUpsertMedication);
        let medResponse: any;
        if (toUpsert.length) {
          medResponse = await PostMedicationRequest(toUpsert, references);
        }
        const basedOnXml = buildBasedOnXmlForDocumentItems(
          meds,
          toUpsert,
          medResponse?.entry,
          "MedicationRequest",
        );
        if (!basedOnXml) throw new Error("basedOn vazio");

        const prescriptionSlot =
          readDocSlot(base64, "special-0") ||
          readDocSlot(base64, "simple-medication") ||
          readDocSlot(base64, "simple-supplement") ||
          readDocSlot(base64, "simple-herbalMedicine");
        const reissuePatientKey =
          patientData?.id?.split("-")?.[1] || patientId;

        await PostDocumentReference(
          {
            ...docReferenceMeta,
            base64: signResponse,
            codeValue: docReferenceMeta.codeValue || "REC_SIM",
            codeDisplay: docReferenceMeta.codeDisplay || "Receituário Simples",
            categoryValue: docReferenceMeta.categoryValue || "prescricao",
            categoryDisplay: docReferenceMeta.categoryDisplay || "Receituário",
          },
          references,
          basedOnXml,
          true,
          prescriptionSlot?.uuid,
          prescriptionSlot?.idValue,
        );
        releaseDocIds(reissuePatientKey, prescriptionSlot?.key);
      } else if (documentType === "solicitation") {
        const services =
          selectedInterventions?.all?.filter(
            (el: any) => el?.type === "service" || el?.type === "procedure",
          ) || [];
        const toUpsert = services.filter(shouldUpsertService);
        let serviceResponse: any;
        if (toUpsert.length) {
          serviceResponse = await PostServiceRequest(
            toUpsert,
            references,
            undefined,
          );
        }
        const basedOnXml = buildBasedOnXmlForDocumentItems(
          services,
          toUpsert,
          serviceResponse?.entry,
          "ServiceRequest",
        );
        if (!basedOnXml) throw new Error("basedOn vazio");

        const solicitationSlot = readFirstDocSlot(
          base64,
          (key) =>
            key === "exam" || key === "procedure" || key.startsWith("exam-"),
        );
        const reissuePatientKey =
          patientData?.id?.split("-")?.[1] || patientId;

        await PostDocumentReference(
          {
            ...docReferenceMeta,
            base64: signResponse,
            codeValue: docReferenceMeta.codeValue || "SERVICE_REQUEST",
            codeDisplay:
              docReferenceMeta.codeDisplay || "Solicitação de exame",
            categoryValue:
              docReferenceMeta.categoryValue || "service-request",
            categoryDisplay:
              docReferenceMeta.categoryDisplay || "Solicitação",
          },
          references,
          basedOnXml,
          true,
          solicitationSlot?.uuid,
          solicitationSlot?.idValue,
        );
        releaseDocIds(reissuePatientKey, solicitationSlot?.key);
      }

      toast.success("Documento reemitido com sucesso!");
      setInterventionsList([]);
      setBase64(undefined);
      setSelectedItem(undefined);
      // Recarrega a lista para exibir o documento recém-reemitido sem F5
      onFinished?.();
      onDone();
    } catch (err) {
      console.error("[ReissueEditInterventionsStep] assinatura:", err);
      toast.warn(
        "Não foi possível realizar a assinatura, por favor tente novamente!",
      );
      // Pode ter havido POST parcial (MedicationRequest/ServiceRequest) — recarrega a lista
      onFinished?.();
    } finally {
      setSigning(false);
    }
  };

  const editContent = loading ? (
    <Stack py={4} alignItems="center">
      <CircularLoading />
    </Stack>
  ) : (
    <Stack spacing={2} width="100%">
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems="flex-start"
        width="100%"
      >
        <Stack flex={1} width="100%" minWidth={0} spacing={1}>
          {documentType === "prescription" ? (
            <PrescriptionCare
              setSelect={openProtocolDialog}
              clean={clean}
              hideProtocolSelect
              allowAdd={false}
            />
          ) : documentType === "solicitation" ? (
            <ExamsFields
              control={control}
              setValue={setValue}
              // Proteção extra: sem item selecionado, nunca chama handleAdd
              action={isEdit ? action : blockedAdd}
              setClean={setClean}
              handleSubmit={handleSubmit}
              isEdit={isEdit}
              allowAdd={false}
            />
          ) : (
            <Typography color="text.secondary">
              Tipo de documento sem edição neste fluxo.
            </Typography>
          )}
        </Stack>

        <Stack flex={1} width="100%" minWidth={0}>
          <ListaDeIntervenções
            selectedInterventions={selectedInterventions}
            functions={functions}
            setCreateDialog={setCreateDialog}
            expandedList={expandedList}
            setExpandedList={setExpandedList}
            appliedProtocol={appliedProtocol}
          />
        </Stack>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        alignItems="center"
      >
        <Button
          variant="text"
          onClick={handleBack}
          sx={{ textTransform: "none" }}
          disabled={signing}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleContinueToSign}
          sx={{ textTransform: "none" }}
          disabled={documentType === "certificate" || signing}
        >
          Assinar documento
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <>
      {/* PDF fora dos dialogs — sobrevive à troca edit ↔ sign */}
      <div style={{ display: "none" }}>
        <DocumentVisualization
          refact
          setBase64={setBase64}
          dataPdf={[
            {
              patientCPF: patientData?.id?.split("-")?.[1] || patientId,
              patientData: patientData?.name?.[0]?.text || patientName || "",
              professionalData: userPractitionerRole,
              // Sem Location do appointment, ainda passa org para customPdf
              location: locationData || {
                managingOrganization: organization?.id
                  ? { reference: `Organization/${organization.id}` }
                  : undefined,
              },
            },
          ]}
        />
      </div>

      <CommonDialog
        isOpen={activeDialog === "edit"}
        handleClose={handleBack}
        maxWidth="lg"
        sheetSize="large"
        sheetTitle={dialogTitle(documentType)}
        sheetSubtitle={
          patientName ? `Paciente: ${patientName}` : undefined
        }
        paperSx={{
          minHeight: "85vh",
          maxHeight: "95vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DialogContent
          sx={{
            pt: 1,
            px: { xs: 1.5, sm: 2.5 },
            pb: 2,
            flex: 1,
            overflow: "auto",
          }}
        >
          {editContent}
        </DialogContent>
      </CommonDialog>

      <SelectProtocolDialog
        setAppliedProtocol={setAppliedProtocol}
        isOpen={activeDialog === "protocol"}
        handleClose={closeProtocolDialog}
        addItem={functions?.handleAdd}
      />

      <SignatureDialog
        visualize={pdfPreviewBase64}
        isOpen={activeDialog === "sign"}
        handleClose={closeSignDialog}
        handleSignature={handleSignature}
        row={{ sign: "Pendente" }}
        loading={signing}
      />
    </>
  );
}
